import { format, isValid } from "date-fns";
import { normalizeCorporateNumber } from "@/utils/corporateNumber";
import type { StoredCommercialPictureSearchParams } from "../types";
import type { StoredPictureSearchFormData } from "../components/storedPictureSearchConditionSchema";

/** 会社法人等番号の入力エラーメッセージ（BEの400メッセージと文言・句点を揃える） */
export const CORPORATE_NUMBER_FORMAT_ERROR_MESSAGE =
  "会社法人等番号は数字（ハイフン区切り可）で入力してください。";

/**
 * 会社法人等番号の入力が検索に使える形式か判定する（正規化後に数字のみ、または空）
 * @param value 入力された会社法人等番号
 * @returns 数字のみ（または空）ならtrue
 */
export const isValidCorporateNumberInput = (value: string): boolean =>
  /^\d*$/.test(normalizeCorporateNumber(value));

/** 取得したユーザーでの絞り込み（不動産登記の取得済み一覧の「取得ユーザー」と同じ選択肢） */
export type StoredPictureAcquirer = "all" | "self" | "other";

/** 取得ユーザーの選択肢。共有ONの会社でだけ表示する */
export const STORED_PICTURE_ACQUIRER_OPTIONS: Array<{
  value: StoredPictureAcquirer;
  label: string;
}> = [
  { value: "all", label: "全て" },
  { value: "self", label: "自分で取得" },
  { value: "other", label: "他ユーザー取得" },
];

/**
 * 取得ユーザーの選択を検索APIの is_other_user_resource に変換する
 * @param acquirer 取得ユーザーの選択
 * @returns 本人以外は true、本人は false、全ては未指定
 */
export const toIsOtherUserResource = (
  acquirer: StoredPictureAcquirer
): boolean | undefined =>
  acquirer === "all" ? undefined : acquirer === "other";

/**
 * 空白のみの文字列を未指定（undefined）として扱い、それ以外は前後の空白を除いて返す
 * @param value 入力文字列
 * @returns トリム後の文字列、または未指定
 */
const trimToUndefined = (value: string): string | undefined => {
  const trimmed = value.trim();
  return trimmed === "" ? undefined : trimmed;
};

/**
 * 日付をAPIパラメータ形式（yyyy-MM-dd）に変換する。未指定・不正な日付はundefined
 * @param date 日付
 * @returns yyyy-MM-dd 形式の文字列、または未指定
 */
const formatDateParam = (date: Date | null): string | undefined =>
  date !== null && isValid(date) ? format(date, "yyyy-MM-dd") : undefined;

/**
 * 検索フォームの値から取得済商業登記検索APIのクエリパラメータを組み立てる。
 * 未指定の条件はパラメータに含めない（BEは指定された条件のみをANDで適用する）。
 * 会社法人等番号の正規化（ハイフン・空白除去）はBE側で行うため、入力値をそのまま送る。
 * 法人名の一致方式（完全/前方/後方/部分）もBEのクエリで適用するため、法人名指定時のみ送る
 * （完全/前方/後方をFEで後段絞り込みすると、BEのページング導入時に取りこぼすため）
 * @param condition 検索フォームの値
 * @returns クエリパラメータ
 */
export const buildStoredCommercialPictureSearchParams = (
  condition: StoredPictureSearchFormData
): StoredCommercialPictureSearchParams => {
  const [acquiredDateFrom, acquiredDateTo] = condition.acquiredDateRange;
  const companyName = trimToUndefined(condition.companyName);
  // 複数選択はカンマ区切りで送る（BEはカンマで分割し、同一項目内はOR）。空配列は送らない
  const joinList = (values: string[]): string | undefined =>
    values.length > 0 ? values.join(",") : undefined;
  const entries: Array<
    [keyof StoredCommercialPictureSearchParams, string | undefined]
  > = [
    ["companyName", companyName],
    // 一致方式は法人名を指定したときだけ意味を持つため、その場合のみ送る
    [
      "companyNameMatchType",
      companyName === undefined ? undefined : condition.companyNameMatchType,
    ],
    ["corporateNumber", trimToUndefined(condition.corporateNumber)],
    ["prefecture", joinList(condition.prefectures)],
    ["city", joinList(condition.cities)],
    ["addressRest", trimToUndefined(condition.addressRest)],
    ["status", joinList(condition.statuses)],
    ["acquiredDateFrom", formatDateParam(acquiredDateFrom)],
    ["acquiredDateTo", formatDateParam(acquiredDateTo)],
  ];
  const specifiedEntries = entries.filter(([, value]) => value !== undefined);
  return Object.fromEntries(
    specifiedEntries
  ) as StoredCommercialPictureSearchParams;
};
